import {
  TCurrency,
  TPaymentTemplate,
  TPayoutTemplate,
} from '../../../../../api/src';
import { TPayoutsURLParams } from './types';
import dayjs from 'dayjs';
import { isNil, omitBy } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';
import { usePagerPropsFromUrl } from '../pager/use-pager-props-from-url';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TPayoutsFiltersForm } from '../../forms/PaymentsFilters/types';
import { useURLContractCurrencies } from '../use-url-contract-currencies';
import { useUIDateFormat } from '../use-ui-dateFormat';
import { useURLContractId } from '../../layouts/ContractIdLayout/ContractIdLayout';
import { useApi } from '../../context/ApiContextProvider';

const templateToPaymentFilters = (
  template: TPayoutTemplate,
  currencies: TCurrency[],
  dateFormat: string,
): TPayoutsURLParams => {
  const tmp = {
    CurrencyCodes: currencies.filter((x) => template.currencyCodes.includes(x)),
    StartAmount: template.startAmount
      ? Number(template.startAmount).toString()
      : undefined,
    EndAmount: template.endAmount
      ? Number(template.endAmount).toString()
      : undefined,
    TransactionStatuses: template.transactionStatuses ?? undefined,
    StartDate: template.startDate
      ? dayjs(template.startDate).format(dateFormat)
      : undefined,
    EndDate: template.endDate
      ? dayjs(template.endDate).format(dateFormat)
      : undefined,
  };
  return omitBy(tmp, isNil);
};

export const useApplyPayoutsTemplate = () => {
  const currencies = useURLContractCurrencies();
  const [, setParams] = useSearchParams();
  const { PageSize } = usePagerPropsFromUrl();
  const dateFormat = useUIDateFormat();
  return useCallback(
    (template: TPayoutTemplate) => {
      const params = {
        ...templateToPaymentFilters(template, currencies, dateFormat),
      };
      if (PageSize !== 10) {
        params.PageSize = PageSize.toString();
      }
      //Page by default is 0
      setParams(params);
    },
    [dateFormat, PageSize, currencies, setParams],
  );
};

export const useDeletePayoutTemplate = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const contractId = useURLContractId();
  return useMutation({
    mutationFn: (id: number) => {
      return api.deleteTemplate(id);
    },
    onSuccess: (data, id) => {
      queryClient.setQueryData(['payouts', 'templates', contractId], (prev) =>
        (prev as TPaymentTemplate[]).filter((x) => x.id !== id),
      );
    },
  });
};

export const useSavePayoutsTemplate = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const contractId = useURLContractId();
  const dateFormat = useUIDateFormat();
  if (!contractId) throw new Error('no contractId');
  return useMutation({
    mutationFn: (filters: TPayoutsFiltersForm) => {
      return api.savePayoutsTemplate(filters, contractId, dateFormat);
    },
    onSuccess: (data) => {
      queryClient.setQueryData<TPaymentTemplate[]>(
        ['payouts', 'templates', contractId],
        (prev) => (prev ? [data, ...prev] : [data]),
      );
    },
  });
};
